import { apiUrl } from '../apiOrigin';

const jsonHeaders = { 'Content-Type': 'application/json' };

async function parseJsonOrText(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { error: text || res.statusText };
  }
}

export async function fetchAuthMe() {
  const res = await fetch(apiUrl('/api/auth/me'), { credentials: 'include' });
  const data = await parseJsonOrText(res);
  if (!res.ok) {
    throw new Error(data.error || 'Session check failed');
  }
  return data;
}

export async function loginRequest(username, password) {
  const res = await fetch(apiUrl('/api/auth/login'), {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ username, password }),
  });
  const data = await parseJsonOrText(res);
  if (!res.ok) {
    throw new Error(data.error || 'Sign in failed');
  }
  return data;
}

export async function signupRequest({ username, email, password, confirmPassword }) {
  const res = await fetch(apiUrl('/api/auth/signup'), {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ username, email, password, confirmPassword }),
  });
  const data = await parseJsonOrText(res);
  if (!res.ok) {
    throw new Error(data.error || 'Sign up failed');
  }
  return data;
}

/**
 * Exchange a Firebase ID token (any provider) for an app session cookie.
 * @param {string} idToken
 * @param {{ username?: string }} [opts] — only for new-account email/password sign-up
 */
export async function exchangeFirebaseIdToken(idToken, opts = {}) {
  const body = { idToken };
  if (opts.username != null && String(opts.username).trim() !== '') {
    body.username = String(opts.username).trim();
  }
  const res = await fetch(apiUrl('/api/auth/firebase'), {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await parseJsonOrText(res);
  if (!res.ok) {
    console.warn('[auth/exchange] /api/auth/firebase failed', {
      status: res.status,
      error: data.error || data,
    });
    const err = new Error(data.error || 'Sign-in failed');
    if (data.code) {
      err.code = data.code;
    }
    throw err;
  }
  console.log('[auth/exchange] /api/auth/firebase OK', {
    userId: data.user?.id,
    username: data.user?.username,
  });
  return data;
}

/**
 * Request a 6-digit verification email (Firebase email/password idToken).
 * @param {string} idToken
 * @param {{ username?: string }} [opts]
 */
export async function sendAppEmailVerificationCode(idToken, opts = {}) {
  const body = { idToken };
  if (opts.username != null && String(opts.username).trim() !== '') {
    body.username = String(opts.username).trim();
  }
  const res = await fetch(apiUrl('/api/auth/email-code/send'), {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await parseJsonOrText(res);
  if (!res.ok) {
    throw new Error(data.error || 'Could not send verification code');
  }
  return data;
}

/**
 * Confirm verification code and receive session cookie.
 * @param {string} idToken
 * @param {string} code
 */
export async function confirmAppEmailVerificationCode(idToken, code) {
  const res = await fetch(apiUrl('/api/auth/email-code/confirm'), {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ idToken, code: String(code).trim() }),
  });
  const data = await parseJsonOrText(res);
  if (!res.ok) {
    throw new Error(data.error || 'Verification failed');
  }
  return data;
}

export async function logoutRequest() {
  const res = await fetch(apiUrl('/api/auth/logout'), {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
  });
  const data = await parseJsonOrText(res);
  if (!res.ok) {
    throw new Error(data.error || 'Logout failed');
  }
  return data;
}
